import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Client, ClientTypes } from '../../../utils/types';

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (client: Client) => void;
}

const AddClientModal: React.FC<AddClientModalProps> = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<Client>({
    clientName: '',
    clientEmail: '',
    location: '',
    clientPhoneNumber: '',
    sn: '',
    orderDate: new Date().toISOString().split('T')[0],
    type: ClientTypes.VENDING, // Default value
    revenue: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    percentage: 0,
    status: true,
    startDate: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: name === 'percentage' ? Number(value) : value,
      }));
    }
  };

  const handleTypeChange = (
    e: import('@mui/material').SelectChangeEvent<ClientTypes>,
  ) => {
    const value = e.target.value as ClientTypes;
    setFormData((prev) => ({ ...prev, type: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      clientName: '',
      clientEmail: '',
      location: '',
      clientPhoneNumber: '',
      sn: '',
      orderDate: new Date().toISOString().split('T')[0],
      type: ClientTypes.VENDING,
      revenue: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      percentage: 0,
      status: true,
      startDate: '',
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-client-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="add-client-modal-title" variant="h6" component="h2">
            Add New Client
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="client-type-label">Client Type</InputLabel>
                <Select
                  labelId="client-type-label"
                  label="Client Type"
                  name="type"
                  value={formData.type}
                  onChange={handleTypeChange}
                  required
                >
                  <MenuItem value={ClientTypes.ATM}>ATM</MenuItem>
                  <MenuItem value={ClientTypes.VENDING}>Vending</MenuItem>
                  <MenuItem value={ClientTypes.AIRBNB}>Airbnb</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Full Name"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                name="clientEmail"
                type="email"
                value={formData.clientEmail}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                name="clientPhoneNumber"
                value={formData.clientPhoneNumber}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            {(formData.type === ClientTypes.ATM || formData.type === ClientTypes.VENDING) && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Percentage"
                    name="percentage"
                    type="number"
                    value={formData.percentage}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Serial Number"
                    name="sn"
                    value={formData.sn}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Order Date"
                    name="orderDate"
                    type="date"
                    value={formData.orderDate}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                required
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AddClientModal;